import React, { useMemo, useState, useContext, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  ScrollView,
  Pressable,
  Alert,
  StyleSheet,
} from "react-native";
import { useTranslation } from "../../i18n";
import api from "../../services/api";
import { AppContext } from "../../../App";
import Header from "../../components/Header";
import DriverManager from "../../components/DriverManager";
import DriverForm from "../../components/DriverForm";
import { uploadDriverDocument } from "../../api/driverDocumentApi";
import { emptyTwoSidedDocument, hasCompleteTwoSidedDocument, uploadTwoSidedDriverDocument } from '../../utils/documentFileUtils';

const COLORS = {
  primary: "#024A59",
  teal: "#1CA7AE",
  background: "#F6FAFB",
  card: "#FFFFFF",
  text: "#0b1a1f",
  muted: "#6B7280",
  error: "#EF4444",
  border: "#E5E7EB",
};

function normalizeDriverDocumentValue(value) {
  if (!value) {
    return {
      front: null,
      back: null,
      pdf: null,
      skipped: false,
    };
  }

  if (
    Object.prototype.hasOwnProperty.call(value, 'front') ||
    Object.prototype.hasOwnProperty.call(value, 'back') ||
    Object.prototype.hasOwnProperty.call(value, 'pdf') ||
    Object.prototype.hasOwnProperty.call(value, 'skipped')
  ) {
    return {
      front: value.front || null,
      back: value.back || null,
      pdf: value.pdf || null,
      skipped: Boolean(value.skipped),
    };
  }

  if (value.uri || value.name || value.mimeType || value.type) {
    return {
      front: value,
      back: null,
      pdf: null,
      skipped: false,
    };
  }

  return {
    front: null,
    back: null,
    pdf: null,
    skipped: false,
  };
}

function pushDriverDocumentUploads(uploadPromises, {
  verificationId,
  personId,
  docType,
  value,
  photoOnly = false,
}) {
  const v = normalizeDriverDocumentValue(value);

  if (!verificationId || !personId || v.skipped) return;

  if (v.pdf && !photoOnly) {
    uploadPromises.push(
      uploadDriverDocument(verificationId, personId, docType, v.pdf, 'pdf')
        .catch((err) =>
          console.error(`Failed to upload ${docType} pdf:`, err)
        )
    );

    return;
  }

  if (v.front) {
    uploadPromises.push(
      uploadDriverDocument(verificationId, personId, docType, v.front, 'front')
        .catch((err) =>
          console.error(`Failed to upload ${docType} front:`, err)
        )
    );
  }

  if (!photoOnly && v.back) {
    uploadPromises.push(
      uploadDriverDocument(verificationId, personId, docType, v.back, 'back')
        .catch((err) =>
          console.error(`Failed to upload ${docType} back:`, err)
        )
    );
  }
}

function isValidDateDDMMYYYY(s) {
  if (!s) return false;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s.trim());
  if (!m) return false;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (yyyy < 1900 || yyyy > 2100) return false;
  if (mm < 1 || mm > 12) return false;
  const daysInMonth = new Date(yyyy, mm, 0).getDate();
  return dd >= 1 && dd <= daysInMonth;
}

function ddmmyyyyToISO(s) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s.trim());
  if (!m) return null;
  const dd = m[1];
  const mm = m[2];
  const yyyy = m[3];
  return `${yyyy}-${mm}-${dd}`;
}

function Card({ children, style }) {
  return <View style={[styles.card, style]}>{children}</View>;
}

function Label({ children, required }) {
  return (
    <Text style={styles.label}>
      {children}
      {required && <Text style={styles.required}> *</Text>}
    </Text>
  );
}

function ErrorText({ children }) {
  if (!children) return null;
  return <Text style={styles.error}>{children}</Text>;
}

function PillRadio({ label, selected, onPress }) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.pill,
        selected && styles.pillOn,
        pressed && styles.pillPressed,
      ]}
    >
      <Text style={[styles.pillText, selected && styles.pillTextOn]}>{label}</Text>
    </Pressable>
  );
}

function OptionRow({ label, selected, onPress }) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.optionRow,
        pressed && styles.optionRowPressed,
      ]}
    >
      <View style={[styles.dot, selected && styles.dotOn]}>
        {selected ? <View style={styles.dotInner} /> : null}
      </View>
      <Text style={styles.optionText}>{label}</Text>
    </Pressable>
  );
}

function CheckRow({ label, checked, onPress }) {
  return (
    <Pressable onPress={onPress} style={styles.checkRow}>
      <View style={[styles.checkBox, checked && styles.checkBoxOn]}>
        {checked ? <Text style={styles.checkMark}>✓</Text> : null}
      </View>
      <Text style={styles.checkLabel}>{label}</Text>
    </Pressable>
  );
}

const s = (v) => (typeof v === "string" ? v : "");

export default function VehicleAdminInfoScreen({ route, navigation }) {
  const { verification, setVerification } = useContext(AppContext);
  const verificationId = verification?.verificationId;
  const { t } = useTranslation();

  
  // ✅ Check if this is driver workflow
  const isDriverWorkflow = verification?.actorRole === "DRIVER";
  
  // ✅ Get matched driver from route params (passed from Find screen)
  const matchedDriver = route.params?.matchedDriver || null;
  const hasExistingDriver = !!matchedDriver;
  
  // ✅ Get driver name from context (entered in NotVehicleOwnerForm)
  const driverName = verification?.driver 
    ? s(`${verification.driver.firstName || ''} ${verification.driver.lastName || ''}`).trim()
    : '';


  // Load saved data from context on component mount
  // const [form, setForm] = useState(() => {
  //   return (
  //     verification?.vehicleAdmin || {
  //       // ---- Document toggles (true = has doc / show + require fields) ----
  //       hasTechInspectionDoc: true,
  //       hasBlueCardDoc: true,
  //       hasOwnerCatLicenseDoc: true,
  //       hasMunicipalityTaxDoc: true,
  //       hasDriverCapacityDoc: true,
  //       hasMacaronDoc: true,
  //       hasSecureBadgeDoc: true,

  //       // ---- Existing fields ----
  //       technicalInspectionValid: null,
  //       technicalInspectionDate: "",
  //       blueCardNumber: "",
  //       blueCardIssueDate: "",
  //       ownerCatLicenseNumber: "",
  //       ownerCatLicenseIssueDate: "",
  //       municipality: "",
  //       municipalityTaxNumber: "",
  //       taxisOperated: "",
  //       driverCapacityNumber: "",
  //       secureBadge: null,
  //       driversRelayCount: "",
  //       macaronNumber: "",
  //       ownerSyndicate: "",
        
  //       // ---- New fields ----
  //       drivers: [], // Array of driver objects
  //     }
  //   );
  // });
  
    const [form, setForm] = useState(() => {
    // 🟢 DRIVER WORKFLOW - minimal fields
    if (isDriverWorkflow) {
      return {
        // Document toggles
        hasTechInspectionDoc: true,
        hasBlueCardDoc: true,
        hasMunicipalityTaxDoc: true,
        hasSecureBadgeDoc: true,
        
        // Fields
        technicalInspectionValid: null,
        technicalInspectionDate: "",
        blueCardNumber: "",
        blueCardIssueDate: "",
        municipality: "",
        municipalityTaxNumber: "",
        secureBadge: null,
        driversRelayCount: "",
        
        // No drivers array needed
      };
    }
    
    // 🟡 OWNER WORKFLOW - full form with drivers
    return (
      verification?.vehicleAdmin || {
        hasTechInspectionDoc: true,
        hasBlueCardDoc: true,
        hasOwnerCatLicenseDoc: true,
        hasMunicipalityTaxDoc: true,
        hasDriverCapacityDoc: true,
        hasMacaronDoc: true,
        hasSecureBadgeDoc: true,
        technicalInspectionValid: null,
        technicalInspectionDate: "",
        blueCardNumber: "",
        blueCardIssueDate: "",
        ownerCatLicenseNumber: "",
        ownerCatLicenseIssueDate: "",
        municipality: "",
        municipalityTaxNumber: "",
        taxisOperated: "",
        driverCapacityNumber: "",
        secureBadge: null,
        driversRelayCount: "",
        macaronNumber: "",
        ownerSyndicate: "",
        drivers: [],
      }
    );
  });

    // 🟢 DRIVER WORKFLOW - If driver was matched, store their personId
  useEffect(() => {
    if (isDriverWorkflow && hasExistingDriver && matchedDriver?.person_id) {
      console.log("✅ Driver matched, storing personId: - VehicleAdminInfoScreen.js:225", matchedDriver.person_id);
      setVerification(prev => ({
        ...prev,
        driverPersonId: matchedDriver.person_id,
        driverMatched: true
      }));
    }
  }, [isDriverWorkflow, hasExistingDriver, matchedDriver]);
  
  // Check if user came from vehicle not matched screen
  const cameFromNotMatched = route.params?.cameFromNotMatched || false;

  const [saving, setSaving] = useState(false);

  // Auto-save form data to context with debouncing
  useEffect(() => {
    const saveTimeout = setTimeout(() => {
      const hasData = Object.values(form).some((value) => {
        if (value === null) return false;
        if (typeof value === "string") return value.trim().length > 0;
        if (Array.isArray(value)) return value.length > 0;
        return true;
      });

      if (hasData) {
        setVerification((prev) => ({
          ...prev,
          vehicleAdmin: form,
        }));
      }
    }, 500);

    return () => clearTimeout(saveTimeout);
  }, [form, setVerification]);

  

  const municipalityOptions = useMemo(
    () => [
      t("vehicleAdmin.arr1"),
      t("vehicleAdmin.arr2"),
      t("vehicleAdmin.arr3"),
      t("vehicleAdmin.arr4"),
      t("vehicleAdmin.arr5"),
      t("vehicleAdmin.arr6"),
      t("vehicleAdmin.arr7"),
    ],
    [t]
  );

  // const errors = useMemo(() => {
  //   const e = {};

  //   // --------------------
  //   // Required fields
  //   // --------------------

  //   // Technical inspection (only if user has the doc)
  //   if (form.hasTechInspectionDoc) {
  //     if (form.technicalInspectionValid === null) {
  //       e.technicalInspectionValid = t("common.required");
  //     }

  //     if (form.technicalInspectionValid === true) {
  //       if (!s(form.technicalInspectionDate).trim()) {
  //         e.technicalInspectionDate = t("common.required");
  //       } else if (!isValidDateDDMMYYYY(form.technicalInspectionDate)) {
  //         e.technicalInspectionDate = t("common.invalidDate");
  //       }
  //     } else if (
  //       form.technicalInspectionDate &&
  //       !isValidDateDDMMYYYY(form.technicalInspectionDate)
  //     ) {
  //       e.technicalInspectionDate = t("common.invalidDate");
  //     }
  //   }

  //   // Blue card (only if doc exists)
  //   if (form.hasBlueCardDoc) {
  //     if (!s(form.blueCardNumber).trim()) e.blueCardNumber = t("common.required");
  //     if (!s(form.blueCardIssueDate).trim()) e.blueCardIssueDate = t("common.required");
  //     if (form.blueCardIssueDate && !isValidDateDDMMYYYY(form.blueCardIssueDate))
  //       e.blueCardIssueDate = t("common.invalidDate");
  //   }

  //   // Owner category license (only if doc exists)
  //   if (form.hasOwnerCatLicenseDoc) {
  //     if (!s(form.ownerCatLicenseNumber).trim())
  //       e.ownerCatLicenseNumber = t("common.required");
  //     if (!s(form.ownerCatLicenseIssueDate).trim())
  //       e.ownerCatLicenseIssueDate = t("common.required");
  //     if (
  //       form.ownerCatLicenseIssueDate &&
  //       !isValidDateDDMMYYYY(form.ownerCatLicenseIssueDate)
  //     )
  //       e.ownerCatLicenseIssueDate = t("common.invalidDate");
  //   }

  //   // Municipality is NOT document-based (keep required)
  //   if (!s(form.municipality).trim()) e.municipality = t("common.required");

  //   // Municipality tax number (doc-based)
  //   if (form.hasMunicipalityTaxDoc) {
  //     if (!s(form.municipalityTaxNumber).trim())
  //       e.municipalityTaxNumber = t("common.required");
  //   }

  //   // taxis operated (keep required)
  //   if (!s(form.taxisOperated).trim()) e.taxisOperated = t("common.required");

  //   // driver capacity number (doc-based)
  //   if (form.hasDriverCapacityDoc) {
  //     if (!s(form.driverCapacityNumber).trim())
  //       e.driverCapacityNumber = t("common.required");
  //   }

  //   // secure badge (doc-based)
  //   if (form.hasSecureBadgeDoc) {
  //     if (form.secureBadge === null) e.secureBadge = t("common.required");
  //   }

  //   // drivers relay count (keep required)
  //   if (!s(form.driversRelayCount).trim()) e.driversRelayCount = t("common.required");

  //   // macaron number (doc-based)
  //   // if (form.hasMacaronDoc) {
  //   //   if (!form.macaronNumber.trim()) e.macaronNumber = t("common.required");
  //   // }

  //   // --------------------
  //   // Driver validation (at least one driver required)
  //   // --------------------
  //   const driversArr = Array.isArray(form.drivers) ? form.drivers : [];

  //   driversArr.forEach((driver, index) => {
  //     if (driver?.birthDate && !isValidDateDDMMYYYY(driver.birthDate)) {
  //       e[`driver${index}BirthDate`] = t("common.invalidDate");
  //     }
  //   });



  //   // --------------------
  //   // Numeric validation
  //   // --------------------
  //   if (form.taxisOperated && isNaN(Number(form.taxisOperated)))
  //     e.taxisOperated = t("vehicleAdmin.invalidNumber");

  //   if (form.driversRelayCount && isNaN(Number(form.driversRelayCount)))
  //     e.driversRelayCount = t("vehicleAdmin.invalidNumber");

  //   return e;
  // }, [form, t]);

  const errors = useMemo(() => {
  const e = {};

  // If this is driver workflow, only validate driver-specific fields
  if (isDriverWorkflow) {
    // Technical inspection (only if user has the doc)
    if (form.hasTechInspectionDoc) {
      if (form.technicalInspectionValid === null) {
        e.technicalInspectionValid = t("common.required");
      }

      if (form.technicalInspectionValid === true) {
        if (!s(form.technicalInspectionDate).trim()) {
          e.technicalInspectionDate = t("common.required");
        } else if (!isValidDateDDMMYYYY(form.technicalInspectionDate)) {
          e.technicalInspectionDate = t("common.invalidDate");
        }
      } else if (
        form.technicalInspectionDate &&
        !isValidDateDDMMYYYY(form.technicalInspectionDate)
      ) {
        e.technicalInspectionDate = t("common.invalidDate");
      }
    }

    // Blue card (only if doc exists)
    if (form.hasBlueCardDoc) {
      if (!s(form.blueCardNumber).trim()) e.blueCardNumber = t("common.required");
      if (!s(form.blueCardIssueDate).trim()) e.blueCardIssueDate = t("common.required");
      if (form.blueCardIssueDate && !isValidDateDDMMYYYY(form.blueCardIssueDate))
        e.blueCardIssueDate = t("common.invalidDate");
    }

    // Municipality is required
    if (!s(form.municipality).trim()) e.municipality = t("common.required");

    // Municipality tax number (doc-based)
    if (form.hasMunicipalityTaxDoc) {
      if (!s(form.municipalityTaxNumber).trim())
        e.municipalityTaxNumber = t("common.required");
    }

    // Secure badge (doc-based)
    // if (form.hasSecureBadgeDoc) {
    //   if (form.secureBadge === null) e.secureBadge = t("common.required");
    // }

    // drivers relay count (required)
    if (!s(form.driversRelayCount).trim()) e.driversRelayCount = t("common.required");
    
    // Numeric validation
    if (form.driversRelayCount && isNaN(Number(form.driversRelayCount)))
      e.driversRelayCount = t("vehicleAdmin.invalidNumber");

    return e;
  }

  // --------------------
  // OWNER WORKFLOW - all the original validation
  // --------------------
  
  // Technical inspection (only if user has the doc)
  if (form.hasTechInspectionDoc) {
    if (form.technicalInspectionValid === null) {
      e.technicalInspectionValid = t("common.required");
    }

    if (form.technicalInspectionValid === true) {
      if (!s(form.technicalInspectionDate).trim()) {
        e.technicalInspectionDate = t("common.required");
      } else if (!isValidDateDDMMYYYY(form.technicalInspectionDate)) {
        e.technicalInspectionDate = t("common.invalidDate");
      }
    } else if (
      form.technicalInspectionDate &&
      !isValidDateDDMMYYYY(form.technicalInspectionDate)
    ) {
      e.technicalInspectionDate = t("common.invalidDate");
    }
  }

  // Blue card (only if doc exists)
  if (form.hasBlueCardDoc) {
    if (!s(form.blueCardNumber).trim()) e.blueCardNumber = t("common.required");
    if (!s(form.blueCardIssueDate).trim()) e.blueCardIssueDate = t("common.required");
    if (form.blueCardIssueDate && !isValidDateDDMMYYYY(form.blueCardIssueDate))
      e.blueCardIssueDate = t("common.invalidDate");
  }

  // Owner category license (only if doc exists)
  if (form.hasOwnerCatLicenseDoc) {
    if (!s(form.ownerCatLicenseNumber).trim())
      e.ownerCatLicenseNumber = t("common.required");
    if (!s(form.ownerCatLicenseIssueDate).trim())
      e.ownerCatLicenseIssueDate = t("common.required");
    if (
      form.ownerCatLicenseIssueDate &&
      !isValidDateDDMMYYYY(form.ownerCatLicenseIssueDate)
    )
      e.ownerCatLicenseIssueDate = t("common.invalidDate");
  }

  // Municipality is NOT document-based (keep required)
  if (!s(form.municipality).trim()) e.municipality = t("common.required");

  // Municipality tax number (doc-based)
  if (form.hasMunicipalityTaxDoc) {
    if (!s(form.municipalityTaxNumber).trim())
      e.municipalityTaxNumber = t("common.required");
  }

  // taxis operated (keep required)
  if (!s(form.taxisOperated).trim()) e.taxisOperated = t("common.required");

  // driver capacity number (doc-based)
  // if (form.hasDriverCapacityDoc) {
  //   if (!s(form.driverCapacityNumber).trim())
  //     e.driverCapacityNumber = t("common.required");
  // }

  // // secure badge (doc-based)
  // if (form.hasSecureBadgeDoc) {
  //   if (form.secureBadge === null) e.secureBadge = t("common.required");
  // }

  // drivers relay count (keep required)
  if (!s(form.driversRelayCount).trim()) e.driversRelayCount = t("common.required");

  // --------------------
  // Driver validation (at least one driver required for owner workflow)
  // --------------------
  const driversArr = Array.isArray(form.drivers) ? form.drivers : [];

  driversArr.forEach((driver, index) => {
    if (driver?.birthDate && !isValidDateDDMMYYYY(driver.birthDate)) {
      e[`driver${index}BirthDate`] = t("common.invalidDate");
    }
  });

  // --------------------
  // Numeric validation
  // --------------------
  if (form.taxisOperated && isNaN(Number(form.taxisOperated)))
    e.taxisOperated = t("vehicleAdmin.invalidNumber");

  if (form.driversRelayCount && isNaN(Number(form.driversRelayCount)))
    e.driversRelayCount = t("vehicleAdmin.invalidNumber");

  return e;
}, [form, t, isDriverWorkflow]); // Add isDriverWorkflow to dependencies

  const canContinue = Object.keys(errors).length === 0 && !saving;

  const update = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
  };

    // 🟢 DRIVER WORKFLOW - Render driver section
  const renderDriverSection = () => {
    if (!isDriverWorkflow) {
      // 🟡 OWNER WORKFLOW - Use existing DriverManager
      return (
        <>
          <Text style={styles.sectionTitle}>{t("vehicleAdmin.driver")}</Text>
          <DriverManager
            vehicleId={verification?.vehicleId}
            verificationId={verificationId}
            initialDrivers={Array.isArray(form.drivers) ? form.drivers : []}
            onDriversChange={(drivers) => {
              update("drivers", drivers);
            }}
          />
        </>
      );
    }

    // 🟢 DRIVER WORKFLOW
    if (hasExistingDriver) {
      // Case 1: Driver already exists in the system
      return (
        <View style={styles.successCard}>
          <View style={styles.successIconContainer}>
            <Text style={styles.successIcon}>✅</Text>
          </View>
          <Text style={styles.successTitle}>
            {t("vehicleAdmin.driverFound")}
          </Text>
          <Text style={styles.successMessage}>
            {t("vehicleAdmin.driverFoundMessage").replace("{{name}}", matchedDriver.name || t("vehicleAdmin.theDriver"))}
          </Text>
          
          <View style={styles.driverInfoBox}>
            <Text style={styles.driverInfoLabel}>
              {t("vehicleAdmin.driverInfo")}
            </Text>
            <Text style={styles.driverInfoName}>{matchedDriver.name}</Text>
            {matchedDriver.phone && (
              <Text style={styles.driverInfoPhone}>{matchedDriver.phone}</Text>
            )}
            {matchedDriver.license_number && (
              <Text style={styles.driverInfoLicense}>
                {t("vehicleAdmin.licenseNumber")}: {matchedDriver.license_number}
              </Text>
            )}
          </View>
        </View>
      );
    } else {
      // Case 2: Driver not found - show form to add themselves
      return (
        <DriverForm
          vehicleId={verification?.vehicleId}
          verificationId={verificationId}
          initialDriverName={driverName}
          onDriverSaved={(personId) => {
            console.log("✅ Driver saved with ID: - VehicleAdminInfoScreen.js:594", personId);
            // Optional: Show success message or auto-save
          }}
        />
      );
    }
  };



    // 🟢 DRIVER WORKFLOW - Submit function (simplified)
  // const submit = async () => {
  //   if (!canContinue) {
  //     Alert.alert(t("vehicleAdmin.form"), t("vehicleAdmin.correctFields"));
  //     return;
  //   }

  //   setSaving(true);
  //   try {
  //     if (isDriverWorkflow) {
  //       // Driver workflow - no drivers array to send
  //       const response = await api.put(`/verification/${verificationId}/extra`, {
  //         vehicle: {
  //           blueCardNumber: form.hasBlueCardDoc ? s(form.blueCardNumber).trim() : null,
  //           blueCardIssueDate: form.hasBlueCardDoc
  //             ? ddmmyyyyToISO(form.blueCardIssueDate)
  //             : null,
  //           municipality: form.municipality.trim(),
  //           municipalityTaxNumber: form.hasMunicipalityTaxDoc
  //             ? form.municipalityTaxNumber.trim()
  //             : null,
  //         },
  //         survey: {
  //           technicalInspectionValid: form.hasTechInspectionDoc
  //             ? form.technicalInspectionValid
  //             : null,
  //           technicalInspectionDate:
  //             form.hasTechInspectionDoc && form.technicalInspectionDate
  //               ? ddmmyyyyToISO(form.technicalInspectionDate)
  //               : null,
  //           secureBadge: form.hasSecureBadgeDoc ? form.secureBadge : null,
  //           driversRelayCount: Number(form.driversRelayCount),
  //         },
  //       });

  //       console.log("Driver vehicle admin info saved: - VehicleAdminInfoScreen.js:487", response.data);
        
  //       // Clear form data
  //       setVerification(prev => ({
  //         ...prev,
  //         vehicleAdmin: null
  //       }));

  //       navigation.navigate("car_registration_doc_upload");
  //     } else {
  //       // 🟡 OWNER WORKFLOW - existing code with drivers array
  //       const driversArr = Array.isArray(form.drivers) ? form.drivers : [];
  //       const driversData = driversArr.map((driver) => ({
  //         name: driver?.name?.trim() || "",
  //         phone: driver?.phone?.trim() || "",
  //         email: driver?.email?.trim() || null,
  //         birthDate: driver?.birthDate || null,
  //         gender: driver?.gender || null,
  //         cinNumber: driver?.cinNumber?.trim() || null,
  //         taxpayerNumber: driver?.taxpayerNumber?.trim() || null,
  //         licenseNumber: driver?.licenseNumber?.trim() || null,
  //         capacityNumber: form?.driverCapacityNumber?.trim() || null,
  //         driverSyndicate: driver?.driverSyndicate?.trim() || null,
  //         personId: driver?.personId || null,
  //         fetchedFromDB: driver?.fetchedFromDB || false,
  //       }));

  //       const response = await api.put(`/verification/${verificationId}/extra`, {
  //         vehicle: {
  //           blueCardNumber: form.hasBlueCardDoc ? s(form.blueCardNumber).trim() : null,
  //           blueCardIssueDate: form.hasBlueCardDoc
  //             ? ddmmyyyyToISO(form.blueCardIssueDate)
  //             : null,
  //           municipality: form.municipality.trim(),
  //           municipalityTaxNumber: form.hasMunicipalityTaxDoc
  //             ? form.municipalityTaxNumber.trim()
  //             : null,
  //           macaronNumber: form.hasMacaronDoc ? form.macaronNumber.trim() : null,
  //         },
  //         owner: {
  //           categoryLicenseNumber: form.hasOwnerCatLicenseDoc
  //             ? form.ownerCatLicenseNumber.trim()
  //             : null,
  //           categoryLicenseIssueDate: form.hasOwnerCatLicenseDoc
  //             ? ddmmyyyyToISO(form.ownerCatLicenseIssueDate)
  //             : null,
  //           taxisOperated: Number(form.taxisOperated),
  //           ownerSyndicate: form.ownerSyndicate?.trim() || null, 
  //         },
  //         drivers: driversData,
  //         survey: {
  //           technicalInspectionValid: form.hasTechInspectionDoc
  //             ? form.technicalInspectionValid
  //             : null,
  //           technicalInspectionDate:
  //             form.hasTechInspectionDoc && form.technicalInspectionDate
  //               ? ddmmyyyyToISO(form.technicalInspectionDate)
  //               : null,
  //           secureBadge: form.hasSecureBadgeDoc ? form.secureBadge : null,
  //           driversRelayCount: Number(form.driversRelayCount),
  //         },
  //       });

  //       // Handle newly created driver IDs
  //       const newlyCreatedPersonIds = response.data?.newlyCreatedPersonIds || [];
        
  //       setVerification((prev) => ({
  //         ...prev,
  //         vehicleAdmin: null,
  //         ownerAddedDriverPersonIds: [
  //           ...(prev.ownerAddedDriverPersonIds || []),
  //           ...newlyCreatedPersonIds
  //         ]
  //       }));

  //       navigation.navigate("car_registration_doc_upload");
  //     }
  //   } catch (err) {
  //     console.error("Submission error: - VehicleAdminInfoScreen.js:565", err);
  //     Alert.alert(
  //       t("vehicleAdmin.error"), 
  //       err.response?.data?.error || t("vehicleAdmin.serverError")
  //     );
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // In VehicleAdminInfoScreen.js - Updated submit function

  const submit = async () => {
    if (!canContinue) {
      Alert.alert(t("vehicleAdmin.form"), t("vehicleAdmin.correctFields"));
      return;
    }

    setSaving(true);
    
    try {
      if (isDriverWorkflow) {
        // Driver workflow - unchanged
        const response = await api.put(`/verification/${verificationId}/extra`, {
          vehicle: {
            blueCardNumber: form.hasBlueCardDoc ? s(form.blueCardNumber).trim() : null,
            blueCardIssueDate: form.hasBlueCardDoc ? ddmmyyyyToISO(form.blueCardIssueDate) : null,
            municipality: form.municipality.trim(),
            municipalityTaxNumber: form.hasMunicipalityTaxDoc ? form.municipalityTaxNumber.trim() : null,
          },
          survey: {
            technicalInspectionValid: form.hasTechInspectionDoc ? form.technicalInspectionValid : null,
            technicalInspectionDate: form.hasTechInspectionDoc && form.technicalInspectionDate 
              ? ddmmyyyyToISO(form.technicalInspectionDate) : null,
            secureBadge: form.hasSecureBadgeDoc ? form.secureBadge : null,
            driversRelayCount: Number(form.driversRelayCount),
          },
        });

        setVerification(prev => ({ ...prev, vehicleAdmin: null }));
        navigation.navigate("car_registration_doc_upload");
        return;
      }

      // ========== OWNER WORKFLOW ==========
      const driversArr = Array.isArray(form.drivers) ? form.drivers : [];
      
      // STEP 1: Prepare driver data for saving
      const driversData = driversArr.map((driver) => ({
        name: driver?.name?.trim() || "",
        phone: driver?.phone?.trim() || "",
        email: driver?.email?.trim() || null,
        birthDate: driver?.birthDate || null,
        gender: driver?.gender || null,
        cinNumber: driver?.cinNumber?.trim() || null,
        taxpayerNumber: driver?.taxpayerNumber?.trim() || null,
        licenseNumber: driver?.licenseNumber?.trim() || null,
        licenseCategory: driver?.licenseCategory || null, // NEW
        capacityNumber: driver?.capacityNumber?.trim() || null,
        driverSyndicate: driver?.driverSyndicate?.trim() || null,
        personId: driver?.personId || null,
        fetchedFromDB: driver?.fetchedFromDB || false,
        // Document status (for tracking, not sent to backend)
        hasDriverPhoto: driver?.hasDriverPhoto || false,
        hasDriverLicense: driver?.hasDriverLicense || false,
        hasDriverIdCard: driver?.hasDriverIdCard || false,
        hasDriverCapacity: driver?.hasDriverCapacity || false,
        hasDriverResidenceCard: hasCompleteTwoSidedDocument(driver?.driverResidenceCardFile),
        hasDriverProfessionalBadge: hasCompleteTwoSidedDocument(driver?.driverProfessionalBadgeFile),
      }));

      // STEP 2: Save driver information first (this will return personIds)
      console.log("Saving driver information... - VehicleAdminInfoScreen.js:787");
      const response = await api.put(`/verification/${verificationId}/extra`, {
        vehicle: {
          blueCardNumber: form.hasBlueCardDoc ? s(form.blueCardNumber).trim() : null,
          blueCardIssueDate: form.hasBlueCardDoc ? ddmmyyyyToISO(form.blueCardIssueDate) : null,
          municipality: form.municipality.trim(),
          municipalityTaxNumber: form.hasMunicipalityTaxDoc ? form.municipalityTaxNumber.trim() : null,
          macaronNumber: form.hasMacaronDoc ? form.macaronNumber.trim() : null,
        },
        owner: {
          categoryLicenseNumber: form.hasOwnerCatLicenseDoc ? form.ownerCatLicenseNumber.trim() : null,
          categoryLicenseIssueDate: form.hasOwnerCatLicenseDoc ? ddmmyyyyToISO(form.ownerCatLicenseIssueDate) : null,
          taxisOperated: Number(form.taxisOperated),
          ownerSyndicate: form.ownerSyndicate?.trim() || null, 
        },
        drivers: driversData,
        survey: {
          technicalInspectionValid: form.hasTechInspectionDoc ? form.technicalInspectionValid : null,
          technicalInspectionDate: form.hasTechInspectionDoc && form.technicalInspectionDate 
            ? ddmmyyyyToISO(form.technicalInspectionDate) : null,
          secureBadge: form.hasSecureBadgeDoc ? form.secureBadge : null,
          driversRelayCount: Number(form.driversRelayCount),
        },
      });

      // Get newly created personIds from response
      const newlyCreatedPersonIds = response.data?.newlyCreatedPersonIds || [];
      console.log("Newly created personIds: - VehicleAdminInfoScreen.js:814", newlyCreatedPersonIds);

      // STEP 3: Upload documents for each driver that has documents selected
      // Map the newly created personIds back to the original drivers
      const driversWithPersonIds = driversArr.map((driver, index) => {
        // If driver already had a personId, use that
        if (driver.personId) {
          return { ...driver, personId: driver.personId };
        }
        // Otherwise, assign from newly created IDs (in the same order)
        return { ...driver, personId: newlyCreatedPersonIds[index] };
      });

      // Upload documents for each driver
      const uploadPromises = [];
      
  //     driversWithPersonIds.forEach((driver) => {
  //       const personId = driver.personId;
  //       if (!personId) return; // Skip if no personId
        
  //       // Check which documents need to be uploaded
  //       // Note: The actual file data needs to be stored somewhere - you'll need to
  //       // modify DriverDocumentUpload to store files in state/driverDocuments
        
  //       if (driver.hasDriverPhoto && driver.driverPhotoFile) {
  // uploadPromises.push(
  //   uploadTwoSidedDriverDocument({
  //     verificationId,
  //     personId,
  //     docType: 'DRIVER_PHOTO',
  //     value: driver.driverPhotoFile,
  //     uploadDriverDocument,
  //   }).catch(err => console.error('Failed to upload driver photo:', err))
  // );
  //       }

  //       if (driver.hasDriverLicense && driver.driverLicenseFile) {
  //         uploadPromises.push(
  //           uploadTwoSidedDriverDocument({ verificationId, personId, docType: 'DRIVER_LICENSE', value: driver.driverLicenseFile, uploadDriverDocument })
  //             .catch(err => console.error('Failed to upload driver license:', err))
  //         );
  //       }

  //       if (driver.hasDriverIdCard && driver.driverIdCardFile) {
  //         uploadPromises.push(
  //           uploadTwoSidedDriverDocument({ verificationId, personId, docType: 'DRIVER_ID_CARD', value: driver.driverIdCardFile, uploadDriverDocument })
  //             .catch(err => console.error('Failed to upload driver ID card:', err))
  //         );
  //       }

  //       if (driver.hasDriverCapacity && driver.driverCapacityFile) {
  //         uploadPromises.push(
  //           uploadTwoSidedDriverDocument({ verificationId, personId, docType: 'DRIVER_CAPACITY', value: driver.driverCapacityFile, uploadDriverDocument })
  //             .catch(err => console.error('Failed to upload driver capacity:', err))
  //         );
  //       }

  //       if (driver.hasDriverResidenceCard && driver.driverResidenceCardFile) {
  //         uploadPromises.push(
  //           uploadTwoSidedDriverDocument({ verificationId, personId, docType: 'DRIVER_RESIDENCE_CARD', value: driver.driverResidenceCardFile, uploadDriverDocument })
  //             .catch(err => console.error('Failed to upload driver residence card:', err))
  //         );
  //       }

  //       if (driver.hasDriverProfessionalBadge && driver.driverProfessionalBadgeFile) {
  //         uploadPromises.push(
  //           uploadTwoSidedDriverDocument({ verificationId, personId, docType: 'DRIVER_PROFESSIONAL_BADGE', value: driver.driverProfessionalBadgeFile, uploadDriverDocument })
  //             .catch(err => console.error('Failed to upload driver professional badge:', err))
  //         );
  //       }
  //     });

          for (const driver of driversWithPersonIds) {
            const personId = driver.personId;

            if (!personId) continue;

            pushDriverDocumentUploads(uploadPromises, {
              verificationId,
              personId,
              docType: 'DRIVER_PHOTO',
              value: driver.driverPhotoFile,
              photoOnly: true,
            });

            pushDriverDocumentUploads(uploadPromises, {
              verificationId,
              personId,
              docType: 'DRIVER_LICENSE',
              value: driver.driverLicenseFile,
            });

            pushDriverDocumentUploads(uploadPromises, {
              verificationId,
              personId,
              docType: 'DRIVER_ID_CARD',
              value: driver.driverIdCardFile,
            });

            pushDriverDocumentUploads(uploadPromises, {
              verificationId,
              personId,
              docType: 'DRIVER_CAPACITY',
              value: driver.driverCapacityFile,
            });

            pushDriverDocumentUploads(uploadPromises, {
              verificationId,
              personId,
              docType: 'DRIVER_RESIDENCE_CARD',
              value: driver.driverResidenceCardFile,
            });

            pushDriverDocumentUploads(uploadPromises, {
              verificationId,
              personId,
              docType: 'DRIVER_PROFESSIONAL_BADGE',
              value: driver.driverProfessionalBadgeFile,
            });
          }

      // Wait for all document uploads to complete (don't block on errors)
      if (uploadPromises.length > 0) {
        console.log(`Uploading ${uploadPromises.length} documents... - VehicleAdminInfoScreen.js:869`);
        await Promise.allSettled(uploadPromises);
      }

      // STEP 4: Update context and navigate
      // setVerification((prev) => ({
      //   ...prev,
      //   vehicleAdmin: null,
      //   ownerAddedDriverPersonIds: [
      //     ...(prev.ownerAddedDriverPersonIds || []),
      //     ...newlyCreatedPersonIds
      //   ]
      // }));
      setVerification((prev) => ({
        ...prev,
        vehicleAdmin: {
          ...prev.vehicleAdmin,
          // Survey data
          technicalInspectionValid: form.technicalInspectionValid,
          technicalInspectionDate: form.technicalInspectionDate,
          secureBadge: form.secureBadge,
          driversRelayCount: form.driversRelayCount,
          
          // Vehicle data
          blueCardNumber: form.blueCardNumber,
          blueCardIssueDate: form.blueCardIssueDate,
          municipality: form.municipality,
          municipalityTaxNumber: form.municipalityTaxNumber,
          macaronNumber: form.macaronNumber,
          
          // Owner data
          ownerCatLicenseNumber: form.ownerCatLicenseNumber,
          ownerCatLicenseIssueDate: form.ownerCatLicenseIssueDate,
          taxisOperated: form.taxisOperated,
          ownerSyndicate: form.ownerSyndicate,
        },
        
        // Save all drivers with their document references
        drivers: driversWithPersonIds.map(driver => ({
          personId: driver.personId,
          name: driver.name,
          phone: driver.phone,
          email: driver.email,
          birthDate: driver.birthDate,
          gender: driver.gender,
          cinNumber: driver.cinNumber,
          taxpayerNumber: driver.taxpayerNumber,
          licenseNumber: driver.licenseNumber,
          licenseCategory: driver.licenseCategory,
          driverSyndicate: driver.driverSyndicate,
          capacityNumber: driver.capacityNumber,
          hasCapacityDoc: driver.hasCapacityDoc,
          hasSecureBadgeDoc: driver.hasSecureBadgeDoc,
          secureBadge: driver.secureBadge,
          hasDriverPhoto: driver.hasDriverPhoto,
          hasDriverLicense: driver.hasDriverLicense,
          hasDriverIdCard: driver.hasDriverIdCard,
          hasDriverCapacity: driver.hasDriverCapacity,
        })),
        
        // Save driver document references
        driverDocuments: {
          ...prev.driverDocuments,
          ...driversWithPersonIds.reduce((acc, driver) => {
            if (driver.personId) {
              acc[driver.personId] = {
                photo: driver.driverPhotoFile,
                license: driver.driverLicenseFile,
                idCard: driver.driverIdCardFile,
                capacity: driver.driverCapacityFile,
              };
            }
            return acc;
          }, {})
        },
        
        ownerAddedDriverPersonIds: [
          ...(prev.ownerAddedDriverPersonIds || []),
          ...newlyCreatedPersonIds
        ]
      }));

      Alert.alert(
        t('common.success'),
        t('driverManager.allDriversSaved', 'Tous les chauffeurs ont été enregistrés avec succès')
      );

      navigation.navigate("car_registration_doc_upload");

    } catch (err) {
      console.error("Submission error: - VehicleAdminInfoScreen.js:959", err);
      Alert.alert(
        t("vehicleAdmin.error"), 
        err.response?.data?.error || t("vehicleAdmin.serverError")
      );
    } finally {
      setSaving(false);
    }
  };

  // Back navigation
  const handleBack = () => {
    navigation.navigate("vehicle_not_found_info");
  };

  // Display driver-specific errors
  const getDriverError = (index, field) => {
    return errors[`driver${index}${field.charAt(0).toUpperCase() + field.slice(1)}`];
  };

  return (
    <View style={styles.screen}>
      <Header />
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {/* Back Button */}
        <Pressable style={styles.backButton} onPress={handleBack}>
          <Text style={styles.backButtonIcon}>←</Text>
        </Pressable>

        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{t("vehicleAdmin.title")}</Text>
          <Text style={styles.subtitle}>{t("vehicleAdmin.subtitle")}</Text>
        </View>

        {/* Tech Inspection Section */}
        <Card>
          <Text style={styles.sectionTitle}>{t("vehicleAdmin.techInspection")}</Text>

          {/* Checkbox label updated to be more intuitive */}
          <CheckRow
            label={t("vehicleAdminDocs.iDontHaveTechInspection")}
            checked={!form.hasTechInspectionDoc}
            onPress={() => {
              const willHave = !form.hasTechInspectionDoc;
              update("hasTechInspectionDoc", willHave);

              if (!willHave) {
                // user does NOT have the doc → clear + unrequire
                update("technicalInspectionValid", null);
                update("technicalInspectionDate", "");
              }
            }}
          />

          {form.hasTechInspectionDoc && (
            <>
              <Label required>{t("vehicleAdmin.techInspectionValid")}</Label>
              <View style={styles.pillRow}>
                <PillRadio
                  label={t("common.yes")}
                  selected={form.technicalInspectionValid === true}
                  onPress={() => update("technicalInspectionValid", true)}
                />
                <PillRadio
                  label={t("common.no")}
                  selected={form.technicalInspectionValid === false}
                  onPress={() => update("technicalInspectionValid", false)}
                />
              </View>
              <ErrorText>{errors.technicalInspectionValid}</ErrorText>

              <Label>{t("vehicleAdmin.techInspectionDate")}</Label>
              <TextInput
                value={form.technicalInspectionDate}
                onChangeText={(v) => update("technicalInspectionDate", v)}
                placeholder="JJ/MM/AAAA"
                placeholderTextColor={COLORS.muted}
                style={[
                  styles.input,
                  errors.technicalInspectionDate && styles.inputError,
                ]}
              />
              <ErrorText>{errors.technicalInspectionDate}</ErrorText>
            </>
          )}
        </Card>

        {/* Vehicle Section */}
        <Card>
          <Text style={styles.sectionTitle}>{t("vehicleAdmin.vehicle")}</Text>

          {/* Blue Card */}
          <CheckRow
            label={t("vehicleAdminDocs.iDontHaveBlueCard")}
            checked={!form.hasBlueCardDoc}
            onPress={() => {
              const willHave = !form.hasBlueCardDoc;
              update("hasBlueCardDoc", willHave);

              if (!willHave) {
                update("blueCardNumber", "");
                update("blueCardIssueDate", "");
              }
            }}
          />

          {form.hasBlueCardDoc && (
            <>
              <Label required>{t("vehicleAdmin.blueCardNumber")}</Label>
              <TextInput
                value={form.blueCardNumber}
                onChangeText={(v) => update("blueCardNumber", v)}
                placeholder={t("vehicleAdmin.blueCardNumberPlaceholder")}
                placeholderTextColor={COLORS.muted}
                style={[
                  styles.input,
                  errors.blueCardNumber && styles.inputError,
                ]}
              />
              <ErrorText>{errors.blueCardNumber}</ErrorText>

              <Label required>{t("vehicleAdmin.blueCardIssueDate")}</Label>
              <TextInput
                value={form.blueCardIssueDate}
                onChangeText={(v) => update("blueCardIssueDate", v)}
                placeholder="JJ/MM/AAAA"
                placeholderTextColor={COLORS.muted}
                style={[
                  styles.input,
                  errors.blueCardIssueDate && styles.inputError,
                ]}
              />
              <ErrorText>{errors.blueCardIssueDate}</ErrorText>
            </>
          )}

          {/* Macaron */}
          {/* <CheckRow
            label={t("vehicleAdminDocs.iDontHaveMacaron")}
            checked={!form.hasMacaronDoc}
            onPress={() => {
              const willHave = !form.hasMacaronDoc;
              update("hasMacaronDoc", willHave);

              if (!willHave) {
                update("macaronNumber", "");
              }
            }}
          />

          {form.hasMacaronDoc && (
            <>
              <Label required>{t("vehicleAdmin.macaronNumber")}</Label>
              <TextInput
                value={form.macaronNumber}
                onChangeText={(v) => update("macaronNumber", v)}
                placeholder={t("vehicleAdmin.macaronNumberPlaceholder")}
                placeholderTextColor={COLORS.muted}
                style={[
                  styles.input,
                  errors.macaronNumber && styles.inputError,
                ]}
              />
              <ErrorText>{errors.macaronNumber}</ErrorText>
            </>
          )} */}
        </Card>

        {/* Owner Section */}
        <Card>
          <Text style={styles.sectionTitle}>{t("vehicleAdmin.owner")}</Text>

          {/* Owner Category License */}
          <CheckRow
            label={t("vehicleAdminDocs.iDontHaveOwnerLicense")}
            checked={!form.hasOwnerCatLicenseDoc}
            onPress={() => {
              const willHave = !form.hasOwnerCatLicenseDoc;
              update("hasOwnerCatLicenseDoc", willHave);

              if (!willHave) {
                update("ownerCatLicenseNumber", "");
                update("ownerCatLicenseIssueDate", "");
              }
            }}
          />

          {form.hasOwnerCatLicenseDoc && (
            <>
              <Label required>{t("vehicleAdmin.ownerCatLicenseNumber")}</Label>
              <TextInput
                value={form.ownerCatLicenseNumber}
                onChangeText={(v) => update("ownerCatLicenseNumber", v)}
                placeholder={t("vehicleAdmin.ownerCatLicenseNumberPlaceholder")}
                placeholderTextColor={COLORS.muted}
                style={[
                  styles.input,
                  errors.ownerCatLicenseNumber && styles.inputError,
                ]}
              />
              <ErrorText>{errors.ownerCatLicenseNumber}</ErrorText>

              <Label required>{t("vehicleAdmin.ownerCatLicenseIssueDate")}</Label>
              <TextInput
                value={form.ownerCatLicenseIssueDate}
                onChangeText={(v) => update("ownerCatLicenseIssueDate", v)}
                placeholder="JJ/MM/AAAA"
                placeholderTextColor={COLORS.muted}
                style={[
                  styles.input,
                  errors.ownerCatLicenseIssueDate && styles.inputError,
                ]}
              />
              <ErrorText>{errors.ownerCatLicenseIssueDate}</ErrorText>
            </>
          )}

          <Label required>{t("vehicleAdmin.municipality")}</Label>
          <View style={styles.optionsBox}>
            {municipalityOptions.map((opt) => (
              <OptionRow
                key={opt}
                label={opt}
                selected={form.municipality === opt}
                onPress={() => update("municipality", opt)}
              />
            ))}
          </View>
          <ErrorText>{errors.municipality}</ErrorText>

          {!cameFromNotMatched && (
            <>
              <Label>{t("vehicleAdmin.ownerSyndicate")}</Label>
              <TextInput
                value={form.ownerSyndicate}
                onChangeText={(v) => update("ownerSyndicate", v)}
                placeholder={t("vehicleAdmin.ownerSyndicatePlaceholder")}
                placeholderTextColor={COLORS.muted}
                style={styles.input}
              />
            </>
          )}


          {/* Municipality tax number (doc-based) */}
          <CheckRow
            label={t("vehicleAdminDocs.iDontHaveMunicipalityTax")}
            checked={!form.hasMunicipalityTaxDoc}
            onPress={() => {
              const willHave = !form.hasMunicipalityTaxDoc;
              update("hasMunicipalityTaxDoc", willHave);

              if (!willHave) {
                update("municipalityTaxNumber", "");
              }
            }}
          />

          {form.hasMunicipalityTaxDoc && (
            <>
              <Label required>{t("vehicleAdmin.municipalityTaxNumber")}</Label>
              <TextInput
                value={form.municipalityTaxNumber}
                onChangeText={(v) => update("municipalityTaxNumber", v)}
                placeholder={t("vehicleAdmin.municipalityTaxNumberPlaceholder")}
                placeholderTextColor={COLORS.muted}
                style={[
                  styles.input,
                  errors.municipalityTaxNumber && styles.inputError,
                ]}
              />
              <ErrorText>{errors.municipalityTaxNumber}</ErrorText>
            </>
          )}

          <Label required>{t("vehicleAdmin.taxisOperated")}</Label>
          <TextInput
            value={form.taxisOperated}
            onChangeText={(v) => update("taxisOperated", v)}
            placeholder={t("vehicleAdmin.taxisOperatedPlaceholder")}
            keyboardType="numeric"
            placeholderTextColor={COLORS.muted}
            style={[styles.input, errors.taxisOperated && styles.inputError]}
          />
          <ErrorText>{errors.taxisOperated}</ErrorText>
        </Card>

        {/* Driver Section */}
        <Card>
          {/* <Text style={styles.sectionTitle}>{t("vehicleAdmin.driver")}</Text> */}
          
          {/* Driver validation error - only for owner workflow */}
          {!isDriverWorkflow && errors.drivers && <ErrorText>{errors.drivers}</ErrorText>}
          
          {/* ✅ CONDITIONAL RENDERING - call renderDriverSection() here */}
          {renderDriverSection()}

          <Label required>{t("vehicleAdmin.driversRelayCount")}</Label>
          <TextInput
            value={form.driversRelayCount}
            onChangeText={(v) => update("driversRelayCount", v)}
            placeholder={t("vehicleAdmin.driversRelayCountPlaceholder")}
            keyboardType="numeric"
            placeholderTextColor={COLORS.muted}
            style={[
              styles.input,
              errors.driversRelayCount && styles.inputError,
            ]}
          />
          <ErrorText>{errors.driversRelayCount}</ErrorText>
        </Card>

        {/* Continue Button */}
        <Pressable
          onPress={submit}
          disabled={!canContinue}
          style={({ pressed }) => [
            styles.cta,
            !canContinue && styles.ctaDisabled,
            pressed && styles.ctaPressed,
          ]}
        >
          <Text style={styles.ctaText}>
            {saving ? t("vehicleAdmin.saving") : t("common.continue")}
          </Text>
        </Pressable>

        <View style={{ height: 32 }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: COLORS.background },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
    maxWidth: 520,
    width: "100%",
    alignSelf: "center",
  },
  backButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    marginTop: 4,
  },
  backButtonIcon: { fontSize: 18, color: COLORS.text },
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: "700", color: COLORS.text, marginBottom: 6 },
  subtitle: { fontSize: 14, color: COLORS.muted, lineHeight: 20 },

  card: {
    backgroundColor: COLORS.card,
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: COLORS.text,
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingBottom: 8,
  },
  label: {
    fontSize: 13,
    fontWeight: "600",
    color: COLORS.text,
    marginBottom: 6,
    marginTop: 8,
  },
  required: { color: COLORS.error },

  input: {
    height: 48,
    borderRadius: 12,
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: COLORS.border,
    paddingHorizontal: 12,
    fontSize: 14,
    color: COLORS.text,
  },
  inputError: { borderColor: COLORS.error, backgroundColor: "#FEF2F2" },
  error: { color: COLORS.error, fontSize: 12, marginTop: 4, marginLeft: 2 },

  pillRow: { flexDirection: "row", gap: 10, marginTop: 8 },
  pill: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: COLORS.card,
  },
  pillOn: { backgroundColor: COLORS.teal, borderColor: COLORS.teal },
  pillPressed: { transform: [{ scale: 0.98 }] },
  pillText: { fontSize: 14, fontWeight: "600", color: COLORS.text },
  pillTextOn: { color: "#FFFFFF" },

  optionsBox: {
    marginTop: 6,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 12,
    padding: 8,
    backgroundColor: "#F9FAFB",
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 8,
    gap: 12,
  },
  optionRowPressed: { backgroundColor: "#F3F4F6" },
  dot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#9CA3AF",
    alignItems: "center",
    justifyContent: "center",
  },
  dotOn: { borderColor: COLORS.teal },
  dotInner: { width: 10, height: 10, borderRadius: 5, backgroundColor: COLORS.teal },
  optionText: { fontSize: 14, color: COLORS.text, fontWeight: "500" },

  // Checkbox row
  checkRow: { flexDirection: "row", alignItems: "center", gap: 10, marginTop: 6, marginBottom: 10 },
  checkBox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
  },
  checkBoxOn: { backgroundColor: COLORS.teal, borderColor: COLORS.teal },
  checkMark: { color: "#fff", fontWeight: "900" },
  checkLabel: { color: COLORS.text, fontWeight: "600" },

  cta: {
    marginTop: 20,
    backgroundColor: COLORS.teal,
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: "center",
    shadowColor: COLORS.teal,
    shadowOpacity: 0.2,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  },
  ctaDisabled: { backgroundColor: "#9CA3AF", opacity: 0.6 },
  ctaPressed: { transform: [{ scale: 0.98 }] },
  ctaText: { color: "#FFFFFF", fontWeight: "700", fontSize: 16 },
  successCard: {
  backgroundColor: '#DCF7E3',
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
  alignItems: 'center',
  borderWidth: 2,
  borderColor: '#16A34A',
},
successIconContainer: {
  marginBottom: 12,
},
successIcon: {
  fontSize: 48,
},
successTitle: {
  fontSize: 18,
  fontWeight: '700',
  color: '#166534',
  marginBottom: 8,
  textAlign: 'center',
},
successMessage: {
  fontSize: 14,
  color: '#15803D',
  textAlign: 'center',
  marginBottom: 16,
  lineHeight: 20,
},
driverInfoBox: {
  backgroundColor: '#FFFFFF',
  borderRadius: 8,
  padding: 16,
  width: '100%',
  borderWidth: 1,
  borderColor: '#86EFAC',
},
driverInfoLabel: {
  fontSize: 12,
  fontWeight: '600',
  color: '#6B7280',
  marginBottom: 8,
  textTransform: 'uppercase',
},
driverInfoName: {
  fontSize: 16,
  fontWeight: '700',
  color: '#166534',
  marginBottom: 4,
},
driverInfoPhone: {
  fontSize: 14,
  color: '#374151',
  marginBottom: 2,
},
driverInfoLicense: {
  fontSize: 14,
  color: '#374151',
},
});